// src/pages/account/NotificationSettings.js
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { Bell, Mail, Calendar, Trophy, Users, TrendingUp } from 'lucide-react';

const TOGGLE_DEFS = [
  {
    key: 'email_transaction_alerts',
    label: 'League transactions',
    description: 'Get notified when any team adds a free agent, completes a trade, or claims a waiver',
    icon: TrendingUp,
  },
  {
    key: 'email_player_news',
    label: 'Player news',
    description: 'Get notified when there is news about players on your roster',
    icon: Bell,
  },
  {
    key: 'email_waiver_results',
    label: 'Waiver results',
    description: 'Get notified when your waiver claims are awarded or denied',
    icon: Trophy,
  },
  {
    key: 'email_trade_offers',
    label: 'Trade proposals',
    description: 'Get notified when you receive or when a trade is accepted or rejected',
    icon: TrendingUp,
  },
  {
    key: 'email_draft_reminders',
    label: 'Draft reminders',
    description: "Get notified when it's your turn to pick",
    icon: Calendar,
  },
  {
    key: 'email_league_updates',
    label: 'League updates',
    description: 'Commissioner announcements and league-wide events',
    icon: Users,
  },
];

const DEFAULT_PREFS = {
  email_transaction_alerts: true,
  email_player_news: true,
  email_waiver_results: true,
  email_trade_offers: true,
  email_draft_reminders: true,
  email_league_updates: true,
};

const NotificationSettings = ({ setMessage }) => {
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'

  // Fetch the user's leagues on mount
  useEffect(() => {
    const loadLeagues = async () => {
      try {
        const response = await leaguesAPI.getMyLeagues();
        const userLeagues = response.leagues || [];
        setLeagues(userLeagues);
        // Auto-select when there is exactly one league — no selector shown
        if (userLeagues.length === 1) {
          setSelectedLeagueId(userLeagues[0].league_id);
        }
      } catch (err) {
        console.error('Error loading leagues:', err);
      } finally {
        setLeaguesLoading(false);
      }
    };
    loadLeagues();
  }, []);

  // Fetch per-league prefs whenever selectedLeagueId changes
  useEffect(() => {
    if (!selectedLeagueId) return;
    const loadPrefs = async () => {
      setPrefsLoading(true);
      try {
        const res = await leaguesAPI.getNotificationPreferences(selectedLeagueId);
        setPrefs(res.preferences || DEFAULT_PREFS);
      } catch (err) {
        console.error('Error loading notification preferences:', err);
        setPrefs(DEFAULT_PREFS);
      } finally {
        setPrefsLoading(false);
      }
    };
    loadPrefs();
  }, [selectedLeagueId]);

  const handleToggle = async (key, newValue) => {
    if (!selectedLeagueId || saveStatus === 'saving') return;
    const updated = { ...prefs, [key]: newValue };
    setSaveStatus('saving');
    try {
      await leaguesAPI.updateNotificationPreferences(selectedLeagueId, updated);
      setPrefs(updated);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setSaveStatus(null);
    }
  };

  const disabled = !selectedLeagueId || prefsLoading || saveStatus === 'saving';

  if (leaguesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (leagues.length === 0) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <Bell className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
        <p className={dynastyTheme.classes.text.neutralLight}>
          You are not a member of any leagues yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* League selector — hidden when only one league (auto-selected seamlessly) */}
      {leagues.length > 1 && (
        <div className={dynastyTheme.components.card.base}>
          <div className="p-6">
            <label className={dynastyTheme.components.label}>League</label>
            <select
              value={selectedLeagueId || ''}
              onChange={e => setSelectedLeagueId(e.target.value || null)}
              className={`${dynastyTheme.components.input} w-full max-w-md`}
            >
              <option value="">— Select a league —</option>
              {leagues.map(league => (
                <option key={league.league_id} value={league.league_id}>
                  {league.league_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Prompt when multiple leagues and none selected yet */}
      {leagues.length > 1 && !selectedLeagueId && (
        <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
          <Bell className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
          <p className={dynastyTheme.classes.text.neutralLight}>
            Select a league above to manage its notification settings.
          </p>
        </div>
      )}

      {/* Notification toggles — shown once a league is selected */}
      {selectedLeagueId && (
        <div className={`${dynastyTheme.components.card.base} p-8`}>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className={`${dynastyTheme.components.heading.h3} flex items-center`}>
                <Mail className="mr-2" size={24} />
                Email Notifications
              </h3>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Choose which updates you want to receive via email for this league
              </p>
            </div>
            <div className="text-sm">
              {saveStatus === 'saving' && (
                <span className={dynastyTheme.classes.text.neutralLight}>Saving...</span>
              )}
              {saveStatus === 'saved' && (
                <span className="text-green-400">Saved ✓</span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {TOGGLE_DEFS.map(({ key, label, description, icon: Icon }) => (
              <div
                key={key}
                className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start">
                  <Icon className="mr-3 mt-1 text-gray-400" size={20} />
                  <div>
                    <p className="font-medium text-white">{label}</p>
                    <p className="text-sm text-gray-400">{description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleToggle(key, !prefs[key])}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors
                    ${prefs[key] ? 'bg-yellow-400' : 'bg-gray-600'}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${prefs[key] ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
